import * as THREE from 'three';
import {
  NERVE_RADIUS_EXAGGERATION,
  SPINAL_LEVELS,
  allNervePaths,
  spinalCurveZ,
  spinalNerveRoots,
  toMesh,
  type NervePath,
  type NervousSystem,
} from './anatomy';
import { mmToMeshUnits } from '../brain/loadCortexMesh';

/** Base colour per subdivision, so the systems stay visually distinguishable. */
const SYSTEM_COLORS: Record<NervousSystem, number> = {
  central: 0x37d6ff,
  somatic: 0x53e0c4,
  autonomic: 0xff9f5a,
  enteric: 0xc98bff,
};

export interface PathwayHandle {
  id: string;
  label: string;
  system: NervousSystem;
  description: string;
  /** World-space curve, used both for rendering and for animating signal pulses. */
  curve: THREE.CatmullRomCurve3;
  material: THREE.MeshBasicMaterial;
  /** Faint outer sheath, giving each trunk a soft halo. */
  glowMaterial: THREE.MeshBasicMaterial;
}

/**
 * The nervous system beyond the brain: spinal cord, spinal nerve roots, and the
 * peripheral, cranial and autonomic pathways.
 *
 * Nerves are tubes rather than lines. WebGL ignores `linewidth` on virtually
 * every platform, so `LineBasicMaterial` always renders one pixel wide — which
 * at whole-body zoom made the peripheral nerves effectively invisible, and gave
 * no way to show that a sciatic nerve is finger-thick while a digital nerve is
 * thread-thin. Tubes carry real relative diameters and hold up when zoomed.
 */
export class NervousSystemView {
  readonly group: THREE.Group;
  readonly pathways: PathwayHandle[] = [];

  private readonly cordMaterial: THREE.MeshBasicMaterial;
  private readonly rootMaterial: THREE.MeshBasicMaterial;

  constructor() {
    this.group = new THREE.Group();

    this.cordMaterial = new THREE.MeshBasicMaterial({
      color: SYSTEM_COLORS.central,
      wireframe: true,
      transparent: true,
      opacity: 0.6,
    });
    this.group.add(new THREE.Mesh(createSpinalCordGeometry(), this.cordMaterial));

    this.rootMaterial = new THREE.MeshBasicMaterial({
      color: SYSTEM_COLORS.somatic,
      transparent: true,
      opacity: 0.5,
    });
    this.group.add(new THREE.Mesh(createSpinalRootGeometry(), this.rootMaterial));

    for (const path of allNervePaths()) {
      for (const side of path.bilateral ? [1, -1] : [1]) {
        this.pathways.push(this.buildPathway(path, side));
      }
    }
  }

  private buildPathway(path: NervePath, side: number): PathwayHandle {
    const points = path.points.map(([x, y, z]) => {
      const [mx, my, mz] = toMesh(x * side, y, z);
      return new THREE.Vector3(mx, my, mz);
    });

    const curve = new THREE.CatmullRomCurve3(points, false, 'catmullrom', 0.4);
    const radius = mmToMeshUnits(path.radiusMm * NERVE_RADIUS_EXAGGERATION) * 0.5;

    // Segment counts scale with trunk size: the sciatic deserves a smooth tube,
    // a 1 mm digital branch does not need the same budget.
    const tubular = path.points.length > 4 ? 56 : 40;
    const radial = path.radiusMm > 5 ? 8 : 5;

    const material = new THREE.MeshBasicMaterial({
      color: SYSTEM_COLORS[path.system],
      transparent: true,
      opacity: 0.75,
    });
    const core = new THREE.Mesh(new THREE.TubeGeometry(curve, tubular, radius, radial, false), material);
    this.group.add(core);

    // A wider, dimmer sheath so nerves still read against the dark background
    // when the whole body is in frame and each trunk is only a few pixels wide.
    const glowMaterial = new THREE.MeshBasicMaterial({
      color: SYSTEM_COLORS[path.system],
      transparent: true,
      opacity: 0.16,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
    });
    const glow = new THREE.Mesh(
      new THREE.TubeGeometry(curve, tubular, radius * 1.8, radial, false),
      glowMaterial,
    );
    this.group.add(glow);

    return {
      id: path.id,
      label: path.label,
      system: path.system,
      description: path.description,
      curve,
      material,
      glowMaterial,
    };
  }

  /**
   * Sets how strongly each subdivision is lit, from 0 (baseline) to 1 (fully
   * engaged), and tints everything toward the active substance's colour.
   *
   * The baseline is deliberately well above zero: an unlit nervous system should
   * still be clearly visible as anatomy, with drug activity brightening it
   * rather than being the only thing that makes it appear.
   */
  setSystemActivity(activity: Record<NervousSystem, number>, tint: THREE.ColorRepresentation): void {
    const tintColor = new THREE.Color(tint);

    const centralLevel = activity.central;
    this.cordMaterial.color.copy(mix(SYSTEM_COLORS.central, tintColor, centralLevel));
    this.cordMaterial.opacity = 0.55 + 0.4 * centralLevel;

    this.rootMaterial.color.copy(mix(SYSTEM_COLORS.somatic, tintColor, activity.somatic));
    this.rootMaterial.opacity = 0.4 + 0.5 * activity.somatic;

    for (const pathway of this.pathways) {
      const level = activity[pathway.system];
      const color = mix(SYSTEM_COLORS[pathway.system], tintColor, level);
      pathway.material.color.copy(color);
      pathway.material.opacity = 0.7 + 0.3 * level;
      pathway.glowMaterial.color.copy(color);
      pathway.glowMaterial.opacity = 0.14 + 0.32 * level;
    }
  }

  setVisible(visible: boolean): void {
    this.group.visible = visible;
  }
}

function mix(base: number, tint: THREE.Color, amount: number): THREE.Color {
  return new THREE.Color(base).lerp(tint, Math.min(1, Math.max(0, amount)) * 0.75);
}

/**
 * The spinal cord: a tube swept along the levels in anatomy.ts, so it carries
 * the real cervical/thoracic/lumbar curvature and the two enlargements rather
 * than being a straight cylinder.
 */
function createSpinalCordGeometry(): THREE.BufferGeometry {
  const points = SPINAL_LEVELS.map(({ y, z }) => {
    const [mx, my, mz] = toMesh(0, y, z);
    return new THREE.Vector3(mx, my, mz);
  });

  const curve = new THREE.CatmullRomCurve3(points, false, 'catmullrom', 0.35);
  const segments = 140;
  const radialSegments = 8;

  const positions: number[] = [];
  const indices: number[] = [];

  const frames = curve.computeFrenetFrames(segments, false);

  for (let i = 0; i <= segments; i++) {
    const t = i / segments;
    const center = curve.getPointAt(t);
    const normal = frames.normals[Math.min(i, frames.normals.length - 1)];
    const binormal = frames.binormals[Math.min(i, frames.binormals.length - 1)];
    const radius = mmToMeshUnits(radiusAtHeight(center.y));

    for (let j = 0; j < radialSegments; j++) {
      const angle = (j / radialSegments) * Math.PI * 2;
      const cos = Math.cos(angle);
      const sin = Math.sin(angle);
      positions.push(
        center.x + radius * (cos * normal.x + sin * binormal.x),
        center.y + radius * (cos * normal.y + sin * binormal.y),
        center.z + radius * (cos * normal.z + sin * binormal.z),
      );
    }
  }

  for (let i = 0; i < segments; i++) {
    for (let j = 0; j < radialSegments; j++) {
      const a = i * radialSegments + j;
      const b = i * radialSegments + ((j + 1) % radialSegments);
      const c = (i + 1) * radialSegments + j;
      const d = (i + 1) * radialSegments + ((j + 1) % radialSegments);
      indices.push(a, c, b, b, c, d);
    }
  }

  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
  geometry.setIndex(indices);
  geometry.computeVertexNormals();
  return geometry;
}

/** Cord radius in mm at a given scene height, interpolated across the levels. */
function radiusAtHeight(meshY: number): number {
  const mmY = meshY / mmToMeshUnits(1);
  const levels = SPINAL_LEVELS;

  if (mmY >= levels[0].y) return levels[0].radius;
  if (mmY <= levels[levels.length - 1].y) return levels[levels.length - 1].radius;

  for (let i = 0; i < levels.length - 1; i++) {
    const a = levels[i];
    const b = levels[i + 1];
    if (mmY <= a.y && mmY >= b.y) {
      const t = (a.y - mmY) / (a.y - b.y);
      return a.radius + (b.radius - a.radius) * t;
    }
  }
  return levels[levels.length - 1].radius;
}

/**
 * The 31 spinal nerve root pairs, as short tapered tubes leaving the cord.
 *
 * Merged into one geometry rather than 62 separate meshes — they never need to
 * be addressed individually, and one draw call is considerably cheaper.
 */
function createSpinalRootGeometry(): THREE.BufferGeometry {
  const positions: number[] = [];
  const indices: number[] = [];
  const radialSegments = 5;
  const rootRadius = mmToMeshUnits(2.6);
  const up = new THREE.Vector3(0, 1, 0);

  const start = new THREE.Vector3();
  const end = new THREE.Vector3();
  const axis = new THREE.Vector3();
  const normal = new THREE.Vector3();
  const binormal = new THREE.Vector3();

  for (const root of spinalNerveRoots()) {
    for (const side of [1, -1]) {
      start.set(...toMesh(0, root.y, spinalCurveZ(root.y)));
      end.set(...toMesh(side * root.reach, root.y - root.reach * 0.35, root.z - 6));

      axis.subVectors(end, start);
      if (axis.lengthSq() < 1e-9) continue;
      axis.normalize();

      normal.crossVectors(axis, up);
      if (normal.lengthSq() < 1e-6) normal.set(1, 0, 0);
      normal.normalize();
      binormal.crossVectors(axis, normal).normalize();

      const base = positions.length / 3;

      for (const [ringIndex, point] of [start, end].entries()) {
        // Roots taper as they leave the cord.
        const radius = rootRadius * (ringIndex === 0 ? 1 : 0.45);
        for (let j = 0; j < radialSegments; j++) {
          const angle = (j / radialSegments) * Math.PI * 2;
          const cos = Math.cos(angle);
          const sin = Math.sin(angle);
          positions.push(
            point.x + radius * (cos * normal.x + sin * binormal.x),
            point.y + radius * (cos * normal.y + sin * binormal.y),
            point.z + radius * (cos * normal.z + sin * binormal.z),
          );
        }
      }

      for (let j = 0; j < radialSegments; j++) {
        const a = base + j;
        const b = base + ((j + 1) % radialSegments);
        const c = base + radialSegments + j;
        const d = base + radialSegments + ((j + 1) % radialSegments);
        indices.push(a, c, b, b, c, d);
      }
    }
  }

  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
  geometry.setIndex(indices);
  geometry.computeVertexNormals();
  return geometry;
}
